import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, cn } from '@vd/ui';
import type { WidgetOverviewItem } from '@vd/types';
import { useAuth } from '../../../features/auth/AuthProvider';
import { api } from '../../../lib/api';
import { WidgetSetupWizard } from '../../../features/widget/WidgetSetupWizard';
import { LAUNCHER_VARIANT_OPTIONS } from '../../../features/widget/widgetOptions';
import { WidgetFlowArt } from '../../../features/widget/WidgetIllustrations';
import styles from './WidgetsOverviewTab.module.css';
import { ProjectMark } from '../../../features/projects/ProjectMark';

/**
 * Company Settings -> Bug report widget. An owner-only overview of every
 * project's widget status: which have it set up, how, and whether it's live,
 * with in-place setup (the same quick-setup modal used in onboarding) and a
 * link to each project's full Widget settings. Owner-gated by the API.
 */
function statusOf(w: WidgetOverviewItem): { label: string; cls: string | undefined } {
  if (!w.hasWidget) return { label: 'Not set up', cls: styles.pillNeutral };
  if (!w.enabled) return { label: 'Off', cls: styles.pillOff };
  if (w.lastSeenAt) return { label: 'Live', cls: styles.pillLive };
  return { label: 'On, not detected', cls: styles.pillPending };
}

function variantLabel(v: WidgetOverviewItem['variant']): string {
  if (!v) return '';
  return LAUNCHER_VARIANT_OPTIONS.find((o) => o.value === v)?.label ?? v;
}

export function WidgetsOverviewTab() {
  const { currentCompanyId } = useAuth();
  const qc = useQueryClient();
  const [setupProjectId, setSetupProjectId] = useState<string | null>(null);

  const { data: rows = [], isLoading, isError } = useQuery({
    queryKey: ['company-widgets', currentCompanyId],
    queryFn: () => (currentCompanyId ? api.listCompanyWidgets(currentCompanyId) : Promise.resolve([])),
    enabled: !!currentCompanyId,
  });

  const closeSetup = (): void => {
    setSetupProjectId(null);
    // The setup wizard may have changed a widget; refresh the overview so the
    // status pill reflects it.
    void qc.invalidateQueries({ queryKey: ['company-widgets', currentCompanyId] });
  };

  return (
    <section className={styles.section}>
      <h2 className={styles.h2}>Bug report widget</h2>
      <p className={styles.sub}>
        The bug-report widget can be installed on any project. Each project has its own widget and
        one-line snippet, so visitors on that site can report a bug straight into its board. Set one
        up here, or from a project&apos;s Settings &rarr; Widget tab.
      </p>

      {/* What the widget is and how it works, opened on request (Jason, 16 Oct); open from the
          start in a workspace with no projects yet, where it is the page's only content (17 Oct). */}
      <details className={styles.learn} data-testid="widget-learn-more" open={!isLoading && !isError && rows.length === 0}>
        <summary>Learn more about how the bug report widget works</summary>
        <div className={styles.learnBody}>
          <ol className={styles.flow}>
            <li>
              <WidgetFlowArt step={1} />
              <strong>A visitor opens it on your site</strong>
              <span>One line of code puts a small launcher on your site. Anyone using the site can open it, no account needed.</span>
            </li>
            <li>
              <WidgetFlowArt step={2} />
              <strong>They show you what is wrong</strong>
              <span>They point at the part of the page that is broken, or record their screen and talk it through, then send it.</span>
            </li>
            <li>
              <WidgetFlowArt step={3} />
              <strong>It lands on your board</strong>
              <span>Each report arrives as a ticket in the project, with a screenshot, the page it came from and the browser&apos;s details.</span>
            </li>
          </ol>
          <p className={styles.learnNote}>
            You choose how it looks, what visitors can do (point, record, or describe), which sites it may run on and which column
            its tickets land in. Turn bug intake on in a project and reports wait in an intake column until your team accepts them.
          </p>
        </div>
      </details>

      {isLoading ? (
        <p className={styles.muted}>Loading&hellip;</p>
      ) : isError ? (
        <p className={styles.muted}>Could not load widgets (owners only).</p>
      ) : rows.length === 0 ? (
        <p className={styles.muted}>No projects yet. Create a project first, then set up its widget.</p>
      ) : (
        <ul className={styles.list}>
          {rows.map((w) => {
            const status = statusOf(w);
            return (
              <li key={w.projectId} className={styles.row}>
                <ProjectMark project={{ id: w.projectId, name: w.projectName, color: w.projectColor }} size={16} />
                <span className={styles.name}>{w.projectName}</span>
                <span className={cn(styles.pill, status.cls)}>{status.label}</span>
                {w.variant && <span className={styles.variant}>{variantLabel(w.variant)}</span>}
                <span className={styles.actions}>
                  <Button variant="secondary" size="sm" onClick={() => setSetupProjectId(w.projectId)}>
                    {w.hasWidget ? 'Manage' : 'Set up'}
                  </Button>
                  <a className={styles.link} href={`/projects/${w.projectId}/settings?tab=widget`}>
                    Full settings
                  </a>
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {/* The setup wizard (TOADO-579), for a widget set up or not: it starts
          from what is saved, and the full settings are a link away. */}
      {setupProjectId && (
        <WidgetSetupWizard key={setupProjectId} open projectId={setupProjectId} onClose={closeSetup} />
      )}
    </section>
  );
}
